import { createFileRoute } from "@tanstack/react-router";
import { lazy, Suspense } from "react";
import { Section, EyebrowHeading } from "@/components/site/Section";
const PartnerInquiryForm = lazy(() => import("@/components/site/partners/PartnerInquiryForm").then(m => ({ default: m.PartnerInquiryForm })));
import { Check, Handshake, Network, Building2, Minus } from "lucide-react";
import { KineticHeading } from "@/components/site/wow/KineticHeading";
const PartnerConstellation = lazy(() => import("@/components/site/wow/PartnerConstellation").then(m => ({ default: m.PartnerConstellation })));
import { SignatureCta } from "@/components/site/wow/SignatureCta";
import { TiltCard } from "@/components/site/wow/TiltCard";
import { GrainOverlay } from "@/components/site/wow/GrainOverlay";
import { Reveal } from "@/components/site/wow/Reveal";
import { aeoGeo } from "@/lib/seo/schema";

export const Route = createFileRoute("/partners")({
  head: () => ({
    meta: [
      { title: "Partnerprogram & samarbeten | Fenny" },
      { name: "description", content: "Tre nivåer: referral, white-label reseller och strategic partner. Tydliga marginaler, NDA inom 24 timmar och ett team byråer faktiskt vill jobba med." },
      { property: "og:title", content: "Partnerprogram | Fenny" },
      { property: "og:description", content: "Bli referral-, reseller- eller strategic partner till Fenny. Marginaler, förmåner och ansökan." },
      { property: "og:url", content: "https://fenny.se/partners" },
    ],
    links: [{ rel: "canonical", href: "https://fenny.se/partners" }],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(aeoGeo({path: "/partners", name: "Partnerprogram & samarbeten | Fenny", description: "Tre nivåer: referral, white-label reseller och strategic partner. Tydliga marginaler, NDA inom 24 timmar och ett team byråer faktiskt vill jobba med."})) },
    ],
  }),
  component: PartnersPage,
});

const tiers = [
  {
    name: "Referral Partner",
    desc: "Tipsar oss om kunder ni inte själva producerar åt. Ni får 10 % av första fakturan — vi tar all leveransrisk.",
    icon: Network,
    fit: "Konsulter, frilansare, byråer utan produktion",
    tone: "butter" as const,
    accent: "text-butter",
  },
  {
    name: "Reseller / White-label",
    desc: "Ni säljer film i eget namn, vi producerar i bakgrunden. NDA inom 24 h, leverans utan Fennys branding, 15–20 % marginal.",
    icon: Building2,
    fit: "Byråer som löpande lägger ut produktion",
    tone: "mint" as const,
    accent: "text-mint",
  },
  {
    name: "Strategic Partner",
    desc: "Återkommande samproduktion, gemensamma case, co-marketing och dedikerad PM. För plattformar och systerbolag.",
    icon: Handshake,
    fit: "Plattformar & återkommande samarbeten",
    tone: "peach" as const,
    accent: "text-peach",
  },
];

type Cell = { value: string; muted?: boolean };
const benefitRows: { label: string; cells: [Cell, Cell, Cell] }[] = [
  { label: "Marginal / ersättning", cells: [{ value: "10 %" }, { value: "15–20 %" }, { value: "Skräddarsytt" }] },
  { label: "NDA inom 24 h", cells: [{ value: "✓" }, { value: "✓" }, { value: "✓" }] },
  { label: "White-label leverans", cells: [{ value: "—", muted: true }, { value: "✓" }, { value: "✓" }] },
  { label: "Prioriterad bokning", cells: [{ value: "—", muted: true }, { value: "—", muted: true }, { value: "✓" }] },
  { label: "Co-marketing & gemensamma case", cells: [{ value: "—", muted: true }, { value: "—", muted: true }, { value: "✓" }] },
  { label: "Dedikerad projektledare", cells: [{ value: "—", muted: true }, { value: "—", muted: true }, { value: "✓" }] },
];

const applySteps = [
  { title: "Skicka intro", body: "Två rader om er och hur ni vill jobba ihop — via formuläret nedan." },
  { title: "20-min intro-call", body: "Vi hörs inom 2 arbetsdagar och stämmer av passform och nivå." },
  { title: "NDA & partneravtal", body: "Signeras digitalt. Enkla villkor, inga inlåsningar." },
  { title: "Onboarding", body: "Pris-PDF, delad Slack-kanal och första brief inom en vecka." },
];

const partnerFaq = [
  { q: "Är ni white-label?", a: "Ja, på Reseller- och Strategic-nivå. Filerna levereras utan vår branding, mejl går via era domäner, och vi visar inte kund i portfolio utan godkännande." },
  { q: "Hur fungerar marginalen?", a: "Referral: 10 % av första fakturan vid stängd affär. Reseller: 15–20 % på fakturerat belopp — eller så fakturerar vi er direkt och ni lägger på er marginal mot slutkund. Strategic skräddarsys per samarbete." },
  { q: "Hur snabbt levererar ni?", a: "En typisk produktionsdag kan oftast bokas inom 2–3 veckor. Klippt material 7–10 arbetsdagar efter inspelning. Strategic Partners har prioriterad kö." },
  { q: "Jobbar ni med stora byråer också?", a: "Ja. Vi är en del av Norrhavet Group och har erfarenhet av att samarbeta med både fristående studios och nätverksbyråer." },
];

const partnerQuote = {
  text: "Vi har Fenny som vår produktionspartner sen två år tillbaka. De levererar i tid, håller kvalitet och får oss att se ut som om vi har ett internt produktionsteam.",
  name: "Marcus L.",
  role: "Creative Director, byråpartner",
};

function PartnersPage() {
  return (
    <div>
      <section className="relative overflow-hidden pt-32 pb-12 md:pt-44 md:pb-16">
        <div
          aria-hidden
          className="absolute inset-0 -z-10 bg-[radial-gradient(60%_60%_at_20%_0%,color-mix(in_oklab,var(--mint)_14%,transparent),transparent_70%)]"
        />
        <GrainOverlay opacity={0.05} />
        <div className="container-wide">
          <div className="max-w-4xl">
            <div className="mb-6 text-xs uppercase tracking-[0.22em] text-mint">Partnerprogram</div>
            <KineticHeading
              text="Vi växer hellre med rätt partners än fler."
              className="text-balance text-5xl font-semibold leading-[1.0] md:text-7xl"
              highlight={{ indices: [3, 4], className: "text-mint italic font-serif font-normal" }}
            />
            <p className="mt-8 max-w-3xl text-pretty text-lg text-muted-foreground md:text-xl">
              Tre tydliga nivåer — referral, white-label reseller och strategic partner.
              Transparenta marginaler, NDA inom 24 timmar och ett team som faktiskt levererar.
            </p>
          </div>
        </div>
      </section>

      <Reveal>
        <Suspense fallback={<div style={{ minHeight: 420 }} />}>
          <PartnerConstellation />
        </Suspense>
      </Reveal>

      {/* Tiers */}
      <Section className="pt-0">
        <div className="mb-12 max-w-2xl">
          <div className="text-xs uppercase tracking-[0.22em] text-peach">Tre nivåer</div>
          <h2 className="mt-3 text-balance text-3xl font-semibold md:text-5xl">
            Välj formen som matchar er.
          </h2>
        </div>
        <div className="grid gap-6 md:grid-cols-3">
          {tiers.map((t) => (
            <TiltCard key={t.name} glow={t.tone} className="h-full">
              <article className="flex h-full flex-col rounded-3xl border border-white/10 bg-surface/70 p-8 backdrop-blur-md md:p-10">
                <span className={`inline-flex h-11 w-11 items-center justify-center rounded-full bg-white/5 ${t.accent}`}>
                  <t.icon className="h-5 w-5" />
                </span>
                <h3 className="mt-6 text-xl font-semibold md:text-2xl">{t.name}</h3>
                <p className="mt-3 flex-1 text-sm leading-relaxed text-foreground/85">{t.desc}</p>
                <div className={`mt-5 text-[11px] uppercase tracking-widest ${t.accent}/80`}>
                  Passar er om: {t.fit}
                </div>
              </article>
            </TiltCard>
          ))}
        </div>
      </Section>

      {/* Benefits table */}
      <Section className="bg-surface/40">
        <EyebrowHeading eyebrow="Förmåner per nivå" title="Vad ingår var?" />
        <div className="mt-10 overflow-hidden rounded-3xl border border-white/10 bg-background/40">
          <div className="grid grid-cols-4 border-b border-white/10 bg-white/[0.03] text-[11px] uppercase tracking-widest text-muted-foreground">
            <div className="p-4 md:p-5">Förmån</div>
            <div className="p-4 text-center md:p-5">Referral</div>
            <div className="p-4 text-center text-mint md:p-5">Reseller</div>
            <div className="p-4 text-center text-peach md:p-5">Strategic</div>
          </div>
          {benefitRows.map((row, i) => (
            <div
              key={row.label}
              className={`grid grid-cols-4 items-center text-sm ${i % 2 === 1 ? "bg-white/[0.015]" : ""}`}
            >
              <div className="p-4 font-medium text-foreground/90 md:p-5">{row.label}</div>
              {row.cells.map((c, j) => (
                <div key={j} className={`p-4 text-center md:p-5 ${c.muted ? "text-muted-foreground" : "text-foreground"}`}>
                  {c.value === "—" ? <Minus className="mx-auto h-4 w-4" /> : c.value}
                </div>
              ))}
            </div>
          ))}
        </div>
      </Section>

      {/* Apply steps */}
      <Section>
        <div className="grid gap-12 md:grid-cols-[1fr_1.1fr] md:gap-16">
          <div>
            <div className="text-xs uppercase tracking-widest text-mint">Så ansöker ni</div>
            <h2 className="mt-3 text-balance text-3xl font-semibold md:text-5xl">
              Fyra steg till påskriven partner.
            </h2>
            <p className="mt-5 text-base text-muted-foreground md:text-lg">
              Vi krånglar inte till det. Ingen lång säljprocess — bara en kort dialog,
              ett enkelt avtal och en handfast onboarding.
            </p>
            <ol className="mt-8 space-y-5">
              {applySteps.map((s, i) => (
                <li key={s.title} className="flex items-start gap-4">
                  <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-mint text-xs font-semibold text-mint-foreground">{i + 1}</span>
                  <div>
                    <div className="text-base font-semibold text-foreground">{s.title}</div>
                    <div className="mt-1 text-sm leading-relaxed text-muted-foreground">{s.body}</div>
                  </div>
                </li>
              ))}
            </ol>
          </div>
          <blockquote className="rounded-3xl border border-white/10 bg-background/40 p-8 md:p-10">
            <p className="text-lg leading-relaxed text-foreground/95 md:text-xl">
              &ldquo;{partnerQuote.text}&rdquo;
            </p>
            <footer className="mt-6 text-sm">
              <div className="font-semibold">{partnerQuote.name}</div>
              <div className="text-muted-foreground">{partnerQuote.role}</div>
            </footer>
            <div className="mt-8 grid grid-cols-2 gap-4 border-t border-white/10 pt-6">
              <div>
                <div className="text-2xl font-semibold text-butter">24 h</div>
                <div className="mt-1 text-[11px] uppercase tracking-widest text-muted-foreground">NDA på plats</div>
              </div>
              <div>
                <div className="text-2xl font-semibold text-butter">15–20 %</div>
                <div className="mt-1 text-[11px] uppercase tracking-widest text-muted-foreground">Reseller-marginal</div>
              </div>
            </div>
          </blockquote>
        </div>
      </Section>

      <Section className="bg-surface/40">
        <EyebrowHeading eyebrow="Snabba svar" title="Det partners brukar fråga" />
        <div className="mt-10 grid gap-4 md:grid-cols-2">
          {partnerFaq.map((f) => (
            <div key={f.q} className="rounded-2xl border border-white/10 bg-surface p-6">
              <div className="flex items-start gap-3">
                <Check className="mt-1 h-4 w-4 shrink-0 text-mint" />
                <div>
                  <h3 className="text-base font-semibold">{f.q}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{f.a}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </Section>

      <Section>
        <div className="mx-auto max-w-3xl">
          <EyebrowHeading
            eyebrow="Ansök till partnerprogrammet"
            title="Skicka en kort intro."
            lede="Välj nivå nedan och berätta i två rader vilka ni är. Vi återkommer inom två arbetsdagar."
            align="center"
          />
          <div className="mt-10">
            <Suspense fallback={<div style={{ minHeight: 520 }} />}>
              <PartnerInquiryForm />
            </Suspense>
          </div>
        </div>
      </Section>

      <SignatureCta
        tone="mint"
        eyebrow="Hellre prata först?"
        title="Boka 30 minuter — vi tar resten."
        body="Vi går igenom vilken nivå som passar er bäst. Kostnadsfritt."
      />
    </div>
  );
}
